import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Empty } from "antd";
import { NotificationManager } from "react-notifications";
import KycQuotationTab from "./KycQuotationTab";
import { fetchQuotationMasterList } from "./kycQuotationShared";

const KycMaterialQuotationTab = ({ productName, search = "", isActive, navigationContext = {} }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [quotationTable, setQuotationTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [quotationSorter, setQuotationSorter] = useState({
		field: "createdOn",
		order: "DESC",
	});

	const loadQuotationList = useCallback(async () => {
		if (!productName) {
			setQuotationTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
			return;
		}

		setIsLoading(true);
		try {
			const result = await fetchQuotationMasterList({
				ispl_product: productName,
				search,
				pageindex: quotationTable.currentPage,
				pagesize: quotationTable.pageSize,
				sortcolumn: quotationSorter.field,
				orderBy: quotationSorter.order,
				groupCompanyId: navigationContext.groupCompanyId || "",
			});

			setQuotationTable((prev) => ({
				...prev,
				rows: result.rows,
				totalRecords: result.totalRecords,
				currentPage: result.currentPage,
			}));
		} catch (error) {
			console.log(error, "Error");
			NotificationManager.error("Unable to load quotation listing.");
			setQuotationTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
		} finally {
			setIsLoading(false);
		}
	}, [
		productName,
		search,
		quotationTable.currentPage,
		quotationTable.pageSize,
		quotationSorter.field,
		quotationSorter.order,
		navigationContext.groupCompanyId,
	]);

	useEffect(() => {
		if (!isActive || !productName) {
			return undefined;
		}
		loadQuotationList();
		return undefined;
	}, [isActive, productName, loadQuotationList]);

	useEffect(() => {
		if (!isActive) {
			return undefined;
		}
		setQuotationTable((prev) => ({ ...prev, currentPage: 1 }));
		return undefined;
	}, [search, isActive]);

	const handleQuotationTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setQuotationTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setQuotationSorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const emptyText = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />;

	const materialDetailsContext = useMemo(
		() => ({
			productName,
			materialName: navigationContext.materialName || productName,
			groupCompanyId: navigationContext.groupCompanyId || "",
			groupCompanyName: navigationContext.groupCompanyName || "",
			materialsCustomer: navigationContext.materialsCustomer || "",
			accountName: navigationContext.accountName || navigationContext.materialsCustomer || "",
			returnTab: navigationContext.returnTab || "materials",
		}),
		[productName, navigationContext]
	);

	return (
		<KycQuotationTab
			tabTableData={quotationTable}
			isLoading={isLoading}
			tableSorter={quotationSorter}
			OnhandleTableSortColumn={handleQuotationTableChange}
			navigationContext={navigationContext}
			materialDetailsContext={materialDetailsContext}
			emptyText={emptyText}
		/>
	);
};

export default KycMaterialQuotationTab;
